import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useMemo } from "react";
import { useEditBankAccountMutation } from "store/apis/bankConnections";
import { Connection, ConnectionBankAccount } from "store/apis/booksConnections";
import { useGetCurrenciesQuery } from "store/apis/invoices";
import { BackendError } from "types/utils/error";

type FormValues = {
  currency: string;
};

type Props = {
  account: ConnectionBankAccount;
  connection: Connection;
  isOpen: boolean;
  close: () => void;
};

export const AssignCurrencyModal = ({
  account,
  connection,
  isOpen,
  close,
}: Props) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();

  const { data: currenciesResponse } = useGetCurrenciesQuery();
  const [editBankAccount, { isLoading }] = useEditBankAccountMutation();

  const currencyOptions = useMemo(() => {
    if (!currenciesResponse) return [];
    return Object.entries(currenciesResponse).map(([code, name]) => ({
      label: `${code} - ${name}`,
      value: code,
    }));
  }, [currenciesResponse]);

  const onSubmit = async (values: FormValues) => {
    try {
      await editBankAccount({
        bankAccountId: account.uuid,
        bankId: connection.uuid,
        groupId,
        entityId,
        payload: { currency: values.currency },
      }).unwrap();
      successToast({ message: "Currency assigned successfully" });
      close();
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content useCustomOverlay asChild size="large">
        <FormikForm
          initialValues={{ currency: account.currency || "USD" }}
          onSubmit={onSubmit}
        >
          {({ values }) => (
            <>
              <Modal.Header>
                <Modal.Title>Assign currency</Modal.Title>
                <Modal.Close />
              </Modal.Header>

              <Modal.Body className="t-flex t-flex-col t-gap-4">
                <div className="t-flex t-flex-col t-gap-1">
                  <span className="t-text-body-sm t-text-text-30">
                    Bank account
                  </span>
                  <span className="t-text-body-sm t-text-text-100">
                    {account.name} {account.account_number}
                  </span>
                </div>

                <Combobox
                  withForm
                  name="currency"
                  label="Select account currency"
                  required
                  options={currencyOptions}
                  value={currencyOptions.find(
                    (val) => val.value === values.currency
                  )}
                  placeholder="Select a currency"
                  menuPortalTarget={document.body}
                  isClearable
                />

                <Banner
                  type="info"
                  description="The currency selected will be permanently assigned to the chosen bank account and cannot be undone."
                />
              </Modal.Body>

              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button type="button">Cancel</Button>
                </Modal.RawClose>
                <Button
                  customType="primary"
                  type="submit"
                  isLoading={isLoading}
                  disabled={isLoading || !values.currency}
                >
                  Confirm
                </Button>
              </Modal.FooterButtonGroup>
            </>
          )}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};
